// src/components/BoardPack/components/consolidate_boardpack_components/Business_Performance_component.jsx
import React from "react";
import { THEME } from "../../constant/color";
import HeadingComponent from "../shared/heading";
import mawgifLogo from "@/assets/img/images.png";

const C = THEME.colors;

/* ─────────────────────────────────────────────────────────────
   Shared helpers
   ───────────────────────────────────────────────────────────── */
const fmt = (val) => {
  if (val === null || val === undefined || val === "") return "";
  if (typeof val === "string") return val; // already formatted (e.g. "3.4%")
  const n = Number(val);
  if (Number.isNaN(n)) return val;
  const abs = Math.abs(n).toLocaleString("en-US");
  return n < 0 ? `(${abs})` : abs;
};

const isNegative = (val) => {
  if (typeof val === "number") return val < 0;
  if (typeof val === "string") return val.trim().startsWith("(") || val.trim().startsWith("-");
  return false;
};

const BORDER = "#808080";
const BORDER_DARK = C.black;

/* ═════════════════════════════════════════════════════════════
   TABLE 1 — Summary table ("ALL COMPANIES")
   label | 4 (Dec Actual/Budget/Var/%Var) | 4 (Dec Actual/PY/Var/%Var)
         | 4 (YTD Actual/PY/Var/%Var)     | 4 (YTD Actual/Budget/Var/%Var)
   No contribution columns, no logo — just the banner row + data.
   ═════════════════════════════════════════════════════════════ */
const LABEL_PCT_SUMMARY = 16;
const DATA_COL_COUNT_SUMMARY = 16;
const DATA_PCT_SUMMARY = (100 - LABEL_PCT_SUMMARY) / DATA_COL_COUNT_SUMMARY;

const SummaryHeaderCell = ({ children, borderRight, rowSpan, widthPct }) => (
  <th
    rowSpan={rowSpan}
    style={{
      width: widthPct ? `${widthPct}%` : undefined,
      padding: "2px 2px",
      fontSize: "7px",
      fontWeight: 700,
      textAlign: "center",
      verticalAlign: rowSpan ? "bottom" : undefined,
      color: C.white,
      backgroundColor: C.blue1,
      borderRight: `1px solid ${borderRight ? BORDER_DARK : "#3a3a8f"}`,
      borderBottom: `1px solid #3a3a8f`,
      whiteSpace: "nowrap",
    }}
  >
    {children}
  </th>
);

const SummaryHeaderValueCells = ({ values = [], keyPrefix }) => {
  const seg = (start) => values.slice(start, start + 4);
  const renderSeg = (start, segKey) =>
    seg(start).map((v, i) => (
      <SummaryHeaderCell key={`${keyPrefix}-${segKey}-${i}`} widthPct={DATA_PCT_SUMMARY} borderRight={i === 3}>
        {v}
      </SummaryHeaderCell>
    ));
  return (
    <>
      {renderSeg(0, "a")}
      {renderSeg(4, "b")}
      {renderSeg(8, "c")}
      {renderSeg(12, "d")}
    </>
  );
};

const SummaryTableHeader = ({ bannerLabel, logoSrc, cornerLabel, headerYears = [], headerMonths = [], headerMeasures = [] }) => (
  <thead>
    <tr>
      <th
        rowSpan={2}
        style={{
          width: `${LABEL_PCT_SUMMARY}%`,
          backgroundColor: C.white,
          borderLeft: `1px solid ${BORDER_DARK}`,
          borderRight: `1px solid ${BORDER_DARK}`,
          borderTop: `1px solid ${BORDER_DARK}`,
          borderBottom: `1px solid ${BORDER}`,
          padding: 0,
          verticalAlign: "middle",
        }}
      >
        {logoSrc && (
          <img
            src={logoSrc}
            alt=""
            style={{ width: "40%", height: "40px", objectFit: "cover", display: "block", margin: "0 auto" }}
          />
        )}
      </th>
      <SummaryHeaderValueCells values={headerYears} keyPrefix="y" />
    </tr>
    <tr>
      <SummaryHeaderValueCells values={headerMonths} keyPrefix="m" />
    </tr>
    <tr>
      <th
        style={{
          backgroundColor: C.blue1,
          color: C.white,
          fontSize: "7px",
          fontWeight: 700,
          textAlign: "center",
          padding: "2px 4px",
          borderLeft: `1px solid ${BORDER_DARK}`,
          borderRight: `1px solid ${BORDER_DARK}`,
          borderTop: `1px solid ${BORDER}`,
          borderBottom: `1px solid #3a3a8f`,
          whiteSpace: "nowrap",
        }}
      >
        {cornerLabel}
      </th>
      <SummaryHeaderValueCells values={headerMeasures} keyPrefix="ms" />
    </tr>
    {/* Contract banner row — only the label cell is filled (black/navy);
        the rest of the row stays plain white, per reference. */}
    <tr style={{ height: "14px" }}>
      <th
        style={{
          height: "14px",
          backgroundColor: C.black,
          color: C.white,
          fontSize: "7px",
          fontWeight: 700,
          textAlign: "center",
          padding: "2px 4px",
          borderLeft: `1px solid ${BORDER_DARK}`,
          borderRight: `1px solid ${BORDER_DARK}`,
          borderTop: `1px solid ${BORDER}`,
          borderBottom: `1px solid ${BORDER_DARK}`,
          whiteSpace: "nowrap",
        }}
      >
        {bannerLabel}
      </th>
      {Array.from({ length: DATA_COL_COUNT_SUMMARY }).map((_, i) => (
        <th
          key={`banner-blank-${i}`}
          style={{
            height: "14px",
            backgroundColor: C.white,
            borderRight: `1px solid ${i % 4 === 3 ? BORDER_DARK : "transparent"}`,
            borderTop: `1px solid ${BORDER}`,
            borderBottom: `1px solid ${BORDER_DARK}`,
            padding: "2px 2px",
            fontSize: "7px",
          }}
        >
          &nbsp;
        </th>
      ))}
    </tr>
  </thead>
);

const SummaryValueCell = ({ value, bg, borderRight, bold, red }) => (
  <td
    style={{
      textAlign: "center",
      padding: "1px 3px",
      fontSize: "7px",
      fontWeight: bold ? 700 : 400,
      color: red || isNegative(value) ? C.red : C.black,
      backgroundColor: bg,
      "--cell-bg": bg,
      borderRight: `1px solid ${borderRight ? BORDER_DARK : BORDER}`,
      borderBottom: `1px solid ${BORDER}`,
      whiteSpace: "nowrap",
    }}
  >
    {fmt(value)}
  </td>
);

const SummaryRow = ({ row }) => {
  const rowBg = row.highlight ? C.silver : C.white;
  // Variance / % Variance columns always use the cream-yellow highlight,
  // regardless of whether the row itself (EBITDA, Operating Profit, etc.)
  // is shaded grey.
  const varianceBg = C.yellowLight;

  const values = row.values || [];
  const seg = (start) => values.slice(start, start + 4);

  return (
    <tr>
      <td
        style={{
          padding: "1px 4px",
          fontSize: "7px",
          fontWeight: row.bold ? 700 : 400,
          color: row.redLabel ? C.red : C.black,
          backgroundColor: rowBg,
          "--cell-bg": rowBg,
          borderLeft: `1px solid ${BORDER_DARK}`,
          borderRight: `1px solid ${BORDER_DARK}`,
          borderBottom: `1px solid ${BORDER}`,
          whiteSpace: "nowrap",
        }}
      >
        {row.label}
      </td>

      {[0, 4, 8, 12].map((start, blockIdx) => {
        const seg4 = seg(start);
        return (
          <React.Fragment key={blockIdx}>
            <SummaryValueCell value={seg4[0]} bg={rowBg} bold={row.bold} />
            <SummaryValueCell value={seg4[1]} bg={rowBg} bold={row.bold} />
            <SummaryValueCell value={seg4[2]} bg={varianceBg} bold={row.bold} />
            <SummaryValueCell value={seg4[3]} bg={varianceBg} borderRight bold={row.bold} />
          </React.Fragment>
        );
      })}
    </tr>
  );
};

const SummaryTable = ({ bannerLabel, logoSrc, cornerLabel = "SAR", rows = [], headerYears, headerMonths, headerMeasures }) => (
  <div style={{ border: `1px solid ${BORDER_DARK}`, width: "100%", overflow: "hidden" }}>
    <table style={{ width: "100%", tableLayout: "fixed", borderCollapse: "separate", borderSpacing: 0 }}>
      <SummaryTableHeader
        bannerLabel={bannerLabel}
        logoSrc={logoSrc}
        cornerLabel={cornerLabel}
        headerYears={headerYears}
        headerMonths={headerMonths}
        headerMeasures={headerMeasures}
      />
      <tbody>
        {rows.map((row, idx) => (
          <SummaryRow key={idx} row={row} />
        ))}
      </tbody>
    </table>
  </div>
);

/* ═════════════════════════════════════════════════════════════
   TABLE 2 — Detail table ("Mawgif Group SAR'000 - Revenue")
   Same shape as the EBITDA / Net Profit tables: label | contribution
   | 4 | 4 | contribution | 4 | 4, with a logo in the title cell and
   a bold GRAND TOTAL row.
   ═════════════════════════════════════════════════════════════ */
const LABEL_PCT = 13;
const CONTRIB_PCT = 4;
const DATA_COL_COUNT = 16;
const DATA_PCT = (100 - LABEL_PCT - CONTRIB_PCT * 2) / DATA_COL_COUNT;

const DetailHeaderCell = ({ children, widthPct, borderRight, rowSpan }) => (
  <th
    rowSpan={rowSpan}
    style={{
      width: widthPct ? `${widthPct}%` : undefined,
      padding: "2px 2px",
      fontSize: "7px",
      fontWeight: 700,
      textAlign: "center",
      verticalAlign: rowSpan ? "top" : undefined,
      color: C.white,
      backgroundColor: C.blue1,
      borderRight: `1px solid ${borderRight ? BORDER_DARK : "#3a3a8f"}`,
      borderBottom: `1px solid #3a3a8f`,
      whiteSpace: "nowrap",
    }}
  >
    {children}
  </th>
);

const DetailHeaderValueCells = ({ values = [], keyPrefix }) => {
  const seg = (start) => values.slice(start, start + 4);
  const renderSeg = (start, segKey) =>
    seg(start).map((v, i) => (
      <DetailHeaderCell key={`${keyPrefix}-${segKey}-${i}`} widthPct={DATA_PCT} borderRight={i === 3}>
        {v}
      </DetailHeaderCell>
    ));
  return (
    <>
      {renderSeg(0, "a")}
      {renderSeg(4, "b")}
    </>
  );
};

const DetailTableHeader = ({ titleLabel, headerYears = [], headerMonths = [], headerMeasures = [] }) => (
  <thead>
    <tr>
      <th
        rowSpan={2}
        style={{
          width: `${LABEL_PCT}%`,
          backgroundColor: C.white,
          borderLeft: `1px solid ${BORDER_DARK}`,
          borderRight: `1px solid ${BORDER_DARK}`,
          borderTop: `1px solid ${BORDER_DARK}`,
          borderBottom: `1px solid ${BORDER}`,
        }}
      />
      <DetailHeaderCell widthPct={CONTRIB_PCT} borderRight rowSpan={3}>
        Contribution
      </DetailHeaderCell>
      <DetailHeaderValueCells values={headerYears.slice(0, 8)} keyPrefix="y1" />
      <DetailHeaderCell widthPct={CONTRIB_PCT} borderRight rowSpan={3}>
        Contribution
      </DetailHeaderCell>
      <DetailHeaderValueCells values={headerYears.slice(8, 16)} keyPrefix="y2" />
    </tr>
    <tr>
      <DetailHeaderValueCells values={headerMonths.slice(0, 8)} keyPrefix="m1" />
      <DetailHeaderValueCells values={headerMonths.slice(8, 16)} keyPrefix="m2" />
    </tr>
    {/* Title cell shares the same row (and same blue) as the Actual/Budget
        measures — matches the reference where the label sits alongside
        the measures row rather than spanning the whole header. */}
    <tr>
      <th
        style={{
          backgroundColor: C.blue1,
          color: C.white,
          borderLeft: `1px solid ${BORDER_DARK}`,
          borderRight: `1px solid ${BORDER_DARK}`,
          borderTop: `1px solid ${BORDER}`,
          borderBottom: `1px solid #3a3a8f`,
          textAlign: "center",
          padding: "2px 4px",
          fontSize: "7px",
          fontWeight: 700,
          whiteSpace: "nowrap",
        }}
      >
        {titleLabel}
      </th>
      <DetailHeaderValueCells values={headerMeasures.slice(0, 8)} keyPrefix="ms1" />
      <DetailHeaderValueCells values={headerMeasures.slice(8, 16)} keyPrefix="ms2" />
    </tr>
    {/* Blank spacer row — matches the summary table's blank banner row
        that sits right before the data rows begin. Give it an explicit
        height + non-breaking space so empty cells don't collapse to 0px. */}
    <tr style={{ height: "14px" }}>
      {Array.from({ length: 1 + 1 + DATA_COL_COUNT / 2 + 1 + DATA_COL_COUNT / 2 }).map((_, i) => {
        const isContribution = i === 0 || i === 1 || i === 10;
        const isGroupEnd = [5, 9, 14, 18].includes(i);
        const showBlackRight = isContribution || isGroupEnd;
        return (
          <th
            key={`detail-blank-${i}`}
            style={{
              height: "14px",
              backgroundColor: C.white,
              borderRight: `1px solid ${showBlackRight ? BORDER_DARK : "transparent"}`,
              borderTop: `1px solid ${BORDER}`,
              borderBottom: `1px solid ${BORDER_DARK}`,
              padding: "2px 2px",
              fontSize: "7px",
              lineHeight: "10px",
            }}
          >
            &nbsp;
          </th>
        );
      })}
    </tr>
  </thead>
);

const DetailValueCell = ({ value, bg, borderRight, bold }) => (
  <td
    style={{
      textAlign: "center",
      padding: "1px 3px",
      fontSize: "7px",
      fontWeight: bold ? 700 : 400,
      color: isNegative(value) ? C.red : C.black,
      backgroundColor: bg,
      "--cell-bg": bg,
      borderRight: `1px solid ${borderRight ? BORDER_DARK : BORDER}`,
      borderBottom: `1px solid ${BORDER}`,
      whiteSpace: "nowrap",
    }}
  >
    {fmt(value)}
  </td>
);

const DetailContributionCell = ({ value, borderRight }) => (
  <td
    style={{
      textAlign: "center",
      padding: "1px 3px",
      fontSize: "7px",
      fontWeight: 700,
      color: isNegative(value) ? C.red : C.white,
      backgroundColor: C.black,
      "--cell-bg": C.black,
      borderRight: `1px solid ${borderRight ? BORDER_DARK : "#333"}`,
      borderBottom: `1px solid #333`,
      whiteSpace: "nowrap",
    }}
  >
    {fmt(value)}
  </td>
);

const DetailBodyRow = ({ row, isGrandTotal }) => {
  const labelBg = isGrandTotal ? C.silver : C.silver;
  const dec4Bg = C.silver;
  const actualPairBg = C.silver;
  const varianceBg = isGrandTotal ? C.silver : C.yellowLight;

  return (
    <tr>
      <td
        style={{
          padding: "1px 4px",
          fontSize: "7px",
          fontWeight: isGrandTotal ? 700 : 400,
          color: isGrandTotal ? C.red : C.black,
          backgroundColor: labelBg,
          "--cell-bg": labelBg,
          borderLeft: `1px solid ${BORDER_DARK}`,
          borderRight: `1px solid ${BORDER_DARK}`,
          borderBottom: `1px solid ${BORDER}`,
          whiteSpace: "nowrap",
        }}
      >
        {row.label}
      </td>

      <DetailContributionCell value={row.contribution1} borderRight />

      <DetailValueCell value={row.block1?.[0]} bg={dec4Bg} bold={isGrandTotal} />
      <DetailValueCell value={row.block1?.[1]} bg={dec4Bg} bold={isGrandTotal} />
      <DetailValueCell value={row.block1?.[2]} bg={varianceBg} bold={isGrandTotal} />
      <DetailValueCell value={row.block1?.[3]} bg={varianceBg} borderRight bold={isGrandTotal} />

      <DetailValueCell value={row.block2?.[0]} bg={actualPairBg} bold={isGrandTotal} />
      <DetailValueCell value={row.block2?.[1]} bg={actualPairBg} bold={isGrandTotal} />
      <DetailValueCell value={row.block2?.[2]} bg={varianceBg} bold={isGrandTotal} />
      <DetailValueCell value={row.block2?.[3]} bg={varianceBg} borderRight bold={isGrandTotal} />

      <DetailContributionCell value={row.contribution2} borderRight />

      <DetailValueCell value={row.ytd1?.[0]} bg={actualPairBg} bold={isGrandTotal} />
      <DetailValueCell value={row.ytd1?.[1]} bg={actualPairBg} bold={isGrandTotal} />
      <DetailValueCell value={row.ytd1?.[2]} bg={varianceBg} bold={isGrandTotal} />
      <DetailValueCell value={row.ytd1?.[3]} bg={varianceBg} borderRight bold={isGrandTotal} />

      <DetailValueCell value={row.ytd2?.[0]} bg={actualPairBg} bold={isGrandTotal} />
      <DetailValueCell value={row.ytd2?.[1]} bg={actualPairBg} bold={isGrandTotal} />
      <DetailValueCell value={row.ytd2?.[2]} bg={varianceBg} bold={isGrandTotal} />
      <DetailValueCell value={row.ytd2?.[3]} bg={varianceBg} borderRight bold={isGrandTotal} />
    </tr>
  );
};

const DetailTable = ({ titleLabel, rows = [], headerYears, headerMonths, headerMeasures }) => (
  <div style={{ border: `1px solid ${BORDER_DARK}`, width: "100%", overflow: "hidden" }}>
    <table style={{ width: "100%", tableLayout: "fixed", borderCollapse: "separate", borderSpacing: 0 }}>
      <DetailTableHeader
        titleLabel={titleLabel}
        headerYears={headerYears}
        headerMonths={headerMonths}
        headerMeasures={headerMeasures}
      />
      <tbody>
        {rows.map((row, idx) => (
          <DetailBodyRow key={idx} row={row} isGrandTotal={!!row.isGrandTotal} />
        ))}
      </tbody>
    </table>
  </div>
);

export { SummaryTable, DetailTable };

/* ─────────────────────────────────────────────────────────────
   Full page — title bar + summary table + detail table + comments box.
   ───────────────────────────────────────────────────────────── */
const Business_Performance_component = ({
  reportTitle,
  sectionTitle,
  logoSrc = mawgifLogo,
  summaryBannerLabel,
  summaryCornerLabel,
  summaryRows,
  summaryHeaderYears,
  summaryHeaderMonths,
  summaryHeaderMeasures,
  detailTitle,
  detailLogoSrc,
  detailRows,
  detailHeaderYears,
  detailHeaderMonths,
  detailHeaderMeasures,
  comments = "",
}) => {
  return (
    <div className="w-full bg-white p-4 flex flex-col gap-3 biz-perf-report" style={{ fontFamily: "Arial, sans-serif" }}>
      {/* Neutralize any inherited zebra-striping (e.g. from a shared <Table> wrapper
          applying tr:nth-child(even) td { background: ... !important }). Every td
          below also sets --cell-bg inline; this rule forces that value to win
          regardless of what specificity/!important the wrapper uses. */}
      <style>{`
        .biz-perf-report table td[style*="--cell-bg"] {
          background-color: var(--cell-bg) !important;
        }
      `}</style>
      {/* Header */}
      <div className="">
        <div>
          <HeadingComponent variant="main">{reportTitle}</HeadingComponent>
          <HeadingComponent variant="sub">{sectionTitle}</HeadingComponent>
        </div>
        <div className="text-right mr-4 font-sm">
          <HeadingComponent variant="cfoSub">CFO Report</HeadingComponent>
        </div>
      </div>

      <SummaryTable
        bannerLabel={summaryBannerLabel}
        logoSrc={logoSrc}
        cornerLabel={summaryCornerLabel}
        rows={summaryRows}
        headerYears={summaryHeaderYears}
        headerMonths={summaryHeaderMonths}
        headerMeasures={summaryHeaderMeasures}
      />

      <DetailTable
        titleLabel={detailTitle}
        rows={detailRows}
        headerYears={detailHeaderYears}
        headerMonths={detailHeaderMonths}
        headerMeasures={detailHeaderMeasures}
      />

      {/* Comments */}
      <div className="mt-0 !border-2 ">
        <div className="border border-black p-2 bg-white">
          <div className="text-center font-semibold text-sm mb-1 border-b border-transparent">Comments</div>
          <textarea
            className="w-full h-12 p-2 resize-none outline-none text-sm focus:ring-0 border-0 bg-transparent"
            defaultValue={comments}
          />
        </div>
      </div>
    </div>
  );
};

export default Business_Performance_component;